import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Drawer,
  IconButton, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageUploader from '../../common/imguploader/ImageUploader';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    background: '#fff',
    fontFamily: 'Heebo',
    '& fieldset': { borderColor: '#E7E7E7' },
    '&:hover fieldset': { borderColor: '#D7D7D7' },
    '&.Mui-focused fieldset': { borderColor: '#065F46' },
  },
  '& .MuiInputLabel-root': { fontFamily: 'Heebo' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#065F46' },
  '& .MuiFormHelperText-root': { fontFamily: 'Heebo', marginLeft: '4px', fontSize: '11px' },
};

const EMPTY = { title: '', description: '', picture: '' };

const ForumModal = ({ open, handleClose, handleSave, editingData }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        title: editingData?.title || '',
        description: editingData?.description || '',
        picture: editingData?.picture || '',
      });
      setTried(false);
    } else {
      setForm(EMPTY);
    }
  }, [open, editingData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (value.trim() !== '' || value === '') {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const onSave = async () => {
    setTried(true);
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await handleSave(form, editingData?._id);
    } finally {
      setSaving(false);
    }
  };

  const titleError = tried && !form.title.trim();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 500 },
          background: '#fff',
          borderTopLeftRadius: { sm: '12px' },
          borderBottomLeftRadius: { sm: '12px' },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* HEADER */}
      <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid #F1F1F1', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0 }}>
        <Box>
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#362B24', fontFamily: 'Heebo', lineHeight: 1.3 }}>
            {editingData ? 'Edit Forum' : 'Create New Forum'}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#8B8B8B', mt: 0.4, fontFamily: 'Heebo' }}>
            {editingData ? 'Update forum details' : 'Start a new discussion thread'}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ border: '1px solid #EBEBEB', width: 36, height: 36, borderRadius: '6px', flexShrink: 0, ml: 1 }}>
          <CloseIcon sx={{ fontSize: 18, color: '#362B24' }} />
        </IconButton>
      </Box>

      {/* BODY */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Cover Image */}
        <Box>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#362B24', fontFamily: 'Heebo', mb: 1 }}>
            Cover Image
          </Typography>
          <Box
            sx={{
              border: '1px dashed #E0E0E0',
              borderRadius: '8px',
              overflow: 'hidden',
              background: '#FAFAFA',
            }}
          >
            <ImageUploader
              data="Upload Forum Image"
              picture={form.picture}
              setPicture={(pic) => setForm((p) => ({ ...p, picture: pic }))}
              avatarSize={80}
            />
          </Box>
        </Box>

        {/* Title */}
        <TextField
          fullWidth
          size="small"
          label="Forum Title"
          name="title"
          value={form.title}
          onChange={handleChange}
          placeholder="Enter a descriptive title"
          required
          error={titleError}
          helperText={titleError ? 'Title is required' : `${form.title.length}/100`}
          inputProps={{ maxLength: 100 }}
          sx={inputSx}
        />

        {/* Description */}
        <TextField
          fullWidth
          multiline
          rows={6}
          label="Description"
          name="description"
          value={form.description}
          onChange={handleChange}
          placeholder="Describe the topic or question for this forum…"
          helperText={`${form.description.length}/500`}
          inputProps={{ maxLength: 500 }}
          sx={{
            ...inputSx,
            '& .MuiOutlinedInput-root': {
              ...inputSx['& .MuiOutlinedInput-root'],
              alignItems: 'flex-start',
            },
          }}
        />
      </Box>

      {/* FOOTER */}
      <Box sx={{ px: 3, py: 2, borderTop: '1px solid #F1F1F1', display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexShrink: 0, background: '#fff' }}>
        <Button
          onClick={handleClose}
          sx={{ borderRadius: '8px', height: '41px', px: 3, border: '1px solid #EBEBEB', color: '#362B24', fontFamily: 'Heebo', textTransform: 'none', fontSize: '14px' }}
        >
          Cancel
        </Button>
        <Button
          onClick={onSave}
          disabled={saving}
          sx={{
            borderRadius: '8px', height: '41px', px: 4, minWidth: 140,
            background: 'linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)',
            color: '#fff', fontFamily: 'Heebo', fontWeight: 600, textTransform: 'none', fontSize: '14px',
            boxShadow: '1px 2px 30px 0px rgba(0,0,0,0.06)',
            '&:hover': { opacity: 0.9, background: 'linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)' },
            '&:disabled': { opacity: 0.6, color: '#fff' },
          }}
        >
          {saving
            ? <CircularProgress size={18} sx={{ color: '#fff' }} />
            : editingData ? 'Update Forum' : 'Create Forum'}
        </Button>
      </Box>
    </Drawer>
  );
};

export default ForumModal;
